/**
 * The image pane: the image the model was actually sent, with zoom and pan.
 *
 * The surface behind the page stays a light, slightly-warm paper in both
 * themes. A white scan on a #16171a background is a flashlight, and
 * `filter: invert()` is not the answer -- an inverted financial statement is
 * unreadable and it changes what a red negative looks like (§12.2).
 */
import { Maximize2, Minus, MoveHorizontal, Plus } from 'lucide-react'
import {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react'
import { IconButton } from '~/components/ui/primitives.tsx'
import { Segmented } from '~/components/ui/tabs.tsx'
import { EmptyState, Command } from '~/components/ui/empty.tsx'
import { useT } from '~/i18n/index.tsx'
import { cn } from '~/lib/cn.ts'

export type Fit = 'width' | 'page' | 'actual'

export type ImageChoice = {
  url: string
  tier: string
  source: string
  index: number
  /** Tier C is two overlapping crops; the band is where they overlap. */
  overlap: number | null
}

/** How far a finger has to travel across the page before it counts as a turn. */
const SWIPE_PX = 60
/** …and how far it may drift off-axis while doing it. */
const SWIPE_SLOP = 40

export function ImagePane({
  pageId, pageNo, choices, choice, onChoice, fit, onFit, onStep,
  renderCommand, missingReason,
}: {
  pageId: number
  pageNo: number
  choices: ImageChoice[]
  choice: ImageChoice | null
  onChoice: (c: ImageChoice) => void
  fit: Fit
  onFit: (f: Fit) => void
  /** Turn the page. Bound to a horizontal swipe, but only at zoom 1. */
  onStep?: (delta: number) => void
  renderCommand: string
  missingReason: string | null
}) {
  const { t } = useT()
  const viewport = useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = useState(1)
  const [showOverlap, setShowOverlap] = useState(false)
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null)
  const [base, setBase] = useState(1)

  // Scale to the fit mode. Recomputed on resize, because the split-pane
  // handle changes this pane's width without a page change.
  const recompute = useCallback(() => {
    const el = viewport.current
    if (!el || !natural) return
    const pad = 24
    const w = (el.clientWidth - pad) / natural.w
    const h = (el.clientHeight - pad) / natural.h
    setBase(fit === 'actual' ? 1 : fit === 'width' ? w : Math.min(w, h))
  }, [natural, fit])

  useLayoutEffect(recompute, [recompute])

  useEffect(() => {
    const el = viewport.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(recompute)
    ro.observe(el)
    return () => ro.disconnect()
  }, [recompute])

  // A new page starts at the top, at the current fit.
  useEffect(() => {
    setZoom(1)
    viewport.current?.scrollTo({ top: 0, left: 0 })
  }, [pageNo])

  // Free zoom on ctrl/⌘+scroll only: a bare wheel must still scroll a tall
  // page, which is the common gesture.
  const onWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return
    e.preventDefault()
    setZoom((z) => clamp(z * (e.deltaY > 0 ? 0.9 : 1.1), 0.2, 8))
  }

  /*
   * Pointer handling, and the one place this pane has to know what kind of
   * pointer it is.
   *
   * With a **mouse**, panning is drag-to-scroll: the viewport has no other
   * way to move sideways at zoom, and a hand cursor over a document is what
   * people expect.
   *
   * With a **finger**, it emphatically is not. The browser already pans a
   * scroll container far better than this can -- with momentum, with rubber
   * banding, on the compositor -- and running both at once is what made the
   * old version scroll at double speed and stutter: `scrollLeft` was being
   * assigned on every `pointermove` *while* native scrolling was also
   * happening, because `touch-action` was never declared and the move was
   * never prevented.
   *
   * So touch gets native scrolling, plus the two gestures the browser does
   * not provide here: pinch to zoom, and a horizontal swipe to turn the
   * page. The swipe is armed only at zoom 1, where there is nothing to pan
   * sideways -- otherwise it would fight panning, which is the gesture that
   * matters more once you are zoomed in.
   */
  const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null)
  const points = useRef(new Map<number, { x: number; y: number }>())
  const pinch = useRef<{ distance: number; zoom: number } | null>(null)
  const swipe = useRef<{ x: number; y: number; time: number } | null>(null)
  const [pinching, setPinching] = useState(false)

  const onPointerDown = (e: React.PointerEvent) => {
    const el = viewport.current
    if (!el) return
    points.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

    if (e.pointerType === 'mouse') {
      if (e.button !== 0) return
      el.setPointerCapture(e.pointerId)
      drag.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop }
      return
    }

    if (points.current.size === 2) {
      // A second finger: this is a pinch, not a scroll. `touch-action: none`
      // goes on now so the browser stops claiming the gesture.
      const [a, b] = [...points.current.values()]
      pinch.current = { distance: distanceBetween(a!, b!), zoom }
      swipe.current = null
      setPinching(true)
    } else if (points.current.size === 1) {
      swipe.current = { x: e.clientX, y: e.clientY, time: e.timeStamp }
    }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const el = viewport.current
    if (!el) return

    if (e.pointerType === 'mouse') {
      const d = drag.current
      if (!d) return
      el.scrollLeft = d.left - (e.clientX - d.x)
      el.scrollTop = d.top - (e.clientY - d.y)
      return
    }

    if (!points.current.has(e.pointerId)) return
    points.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

    const p = pinch.current
    if (p && points.current.size === 2) {
      const [a, b] = [...points.current.values()]
      const now = distanceBetween(a!, b!)
      if (p.distance > 0) setZoom(clamp((p.zoom * now) / p.distance, 0.2, 8))
    }
    // A one-finger move is left entirely to the browser.
  }

  const endPointer = (e: React.PointerEvent) => {
    const el = viewport.current
    const start = swipe.current
    const wasPinching = pinch.current !== null

    points.current.delete(e.pointerId)
    if (points.current.size < 2) {
      pinch.current = null
      setPinching(false)
    }

    if (e.pointerType === 'mouse') {
      drag.current = null
      try { el?.releasePointerCapture(e.pointerId) } catch {}
      return
    }

    // Swipe to turn the page: only at rest, only from one finger, only when
    // the gesture is decisively horizontal, and never after a pinch.
    if (start && !wasPinching && onStep && zoom === 1 && points.current.size === 0) {
      const dx = e.clientX - start.x
      const dy = e.clientY - start.y
      const quick = e.timeStamp - start.time < 800
      if (quick && Math.abs(dx) > SWIPE_PX && Math.abs(dy) < SWIPE_SLOP) {
        // Physical, not logical: the document does not mirror in Arabic, so
        // dragging the paper leftwards always means "the next page".
        onStep(dx < 0 ? 1 : -1)
      }
    }
    swipe.current = null
  }

  const scale = base * zoom
  const tiers = [...new Set(choices.map((c) => c.tier))]

  return (
    <section className="flex h-full min-w-0 flex-col bg-[var(--bg-sunken)]">
      <div
        ref={viewport}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        className={cn(
          'min-h-0 flex-1 overflow-auto overscroll-contain p-3',
          // The browser owns one-finger panning; it is taken away only for
          // the duration of a pinch, when two fingers are down.
          pinching ? 'touch-none' : 'touch-pan-x touch-pan-y',
          drag.current ? 'cursor-grabbing' : 'cursor-grab',
        )}
      >
        {!choice ? (
          <div className="p-4">
            <EmptyState title={t('viewer.noImage')}>
              {missingReason === 'not_planned'
                ? t('viewer.skippedPageWhy', { pageType: '' })
                : t('viewer.imagePreparing')}
              <div className="mt-2">{t('viewer.imageCommand')}</div>
              <Command>{renderCommand}</Command>
            </EmptyState>
          </div>
        ) : (
          <div className="flex min-h-full justify-center">
            <div className="relative shrink-0">
              <img
                key={choice.url}
                src={choice.url}
                alt={t('viewer.page', { n: pageNo })}
                onLoad={(e) => {
                  const img = e.currentTarget
                  setNatural({ w: img.naturalWidth, h: img.naturalHeight })
                }}
                draggable={false}
                className="block h-auto select-none bg-[var(--paper)] shadow-none ring-1 ring-[var(--border-strong)]"
                style={natural ? { width: natural.w * scale } : { width: '100%' }}
              />
              {/* "The row is missing" and "the row was cut by the crop" look
                  identical until you can see where the cut was (§10.4). */}
              {showOverlap && choice.overlap !== null && natural && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 border-y border-dashed border-[var(--accent)] bg-[var(--accent)]/10"
                  style={
                    choice.index === 0
                      ? { bottom: 0, height: `${choice.overlap * 100}%` }
                      : { top: 0, height: `${choice.overlap * 100}%` }
                  }
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* --- controls --------------------------------------------------- */}
      <div className="flex flex-wrap items-center gap-2 border-t border-[var(--border)] bg-[var(--bg-raised)] px-2 py-1.5">
        <Segmented
          size="sm"
          ariaLabel={t('viewer.fitWidth')}
          value={fit}
          onChange={(f) => { onFit(f); setZoom(1) }}
          options={[
            { id: 'width' as const, label: t('viewer.fitWidth'), title: 'f' },
            { id: 'page' as const, label: t('viewer.fitPage'), title: '0' },
            { id: 'actual' as const, label: '1:1', title: '1' },
          ]}
        />

        <div className="flex items-center gap-0.5">
          <IconButton label={t('viewer.zoomOut')} size="sm" onClick={() => setZoom((z) => clamp(z * 0.85, 0.2, 8))}>
            <Minus size={14} />
          </IconButton>
          <span className="tnum w-10 text-center text-[length:var(--text-micro)] text-[var(--text-muted)]">
            {Math.round(scale * 100)}%
          </span>
          <IconButton label={t('viewer.zoomIn')} size="sm" onClick={() => setZoom((z) => clamp(z * 1.15, 0.2, 8))}>
            <Plus size={14} />
          </IconButton>
          <IconButton label={t('viewer.fitPage')} size="sm" onClick={() => { onFit('page'); setZoom(1) }}>
            <Maximize2 size={14} />
          </IconButton>
        </div>

        <div className="flex-1" />

        {/* Only tiers that exist for *this* page get a button. */}
        {tiers.length > 1 && (
          <Segmented
            size="sm"
            ariaLabel={t('viewer.tier')}
            value={choice?.tier ?? tiers[0]!}
            onChange={(tier) => {
              const next = choices.find((x) => x.tier === tier)
              if (next) onChoice(next)
            }}
            options={tiers.map((tier) => ({ id: tier, label: tier }))}
          />
        )}

        {/* Tier C is two crops; they get their own buttons, plus the band. */}
        {choice && choices.filter((x) => x.tier === choice.tier).length > 1 && (
          <>
            <Segmented
              size="sm"
              ariaLabel={t('viewer.tier')}
              value={String(choice.index)}
              onChange={(i) => {
                const next = choices.find((x) => x.tier === choice.tier && String(x.index) === i)
                if (next) onChoice(next)
              }}
              options={choices
                .filter((x) => x.tier === choice.tier)
                .map((x) => ({ id: String(x.index), label: `${x.tier}${x.index + 1}` }))}
            />
            <IconButton
              label={t('viewer.overlap')}
              size="sm"
              active={showOverlap}
              onClick={() => setShowOverlap((s) => !s)}
            >
              <MoveHorizontal size={14} />
            </IconButton>
          </>
        )}

        {choice && (
          <span
            className="text-[length:var(--text-micro)] text-[var(--text-faint)]"
            title={`page id ${pageId}`}
          >
            {choice.source === 'extraction' ? t('viewer.asSent') : t('viewer.fullPage')}
          </span>
        )}
      </div>
    </section>
  )
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

const distanceBetween = (a: { x: number; y: number }, b: { x: number; y: number }) =>
  Math.hypot(a.x - b.x, a.y - b.y)
